<div class="create-form-container h-full">
  <!-- Tabs -->
  <p-tabs [(value)]="activeTab" class="mb-4" >
    <p-tablist>
      <p-tab [value]="0">
        <i class="pi pi-info-circle mr-2"></i>Basic Information
      </p-tab>
      <p-tab [value]="1" [disabled]="!form.module_id">
        <i class="pi pi-th-large mr-2"></i>Form Builder
      </p-tab>
    </p-tablist>

    <p-tabpanels>
      <!-- Tab 1: Basic Information -->
      <p-tabpanel [value]="0">
        <div class="grid grid-cols-1 gap-6 p-4">
          <div>
            <label class="block mb-2 font-semibold">Form Name <span class="text-red-500">*</span></label>
            <input pInputText [(ngModel)]="form.form_name" class="w-full" 
              placeholder="Enter form name (e.g., Lead Capture Form)" />
          </div>

          <div>
            <label class="block mb-2 font-semibold">Module <span class="text-red-500">*</span></label>
            <p-select [(ngModel)]="form.module_name" [options]="availableModules" 
              optionLabel="name" optionValue="name" class="w-full"
              placeholder="Select a module" (onChange)="onModuleSelected()"
              [disabled]="isEditMode"
              appendTo="body"
              >
            </p-select>
            <small class="text-gray-500">Select which module this form belongs to</small>
          </div>

          <div>
            <label class="block mb-2 font-semibold">Description</label>
            <textarea pInputTextarea [(ngModel)]="form.description" class="w-full" rows="3"
              placeholder="Enter form description (optional)"></textarea>
          </div>

          <div class="flex justify-between gap-2 mt-6 pt-4 border-t">
            <div></div>
            <div class="flex gap-2">
              <p-button label="Cancel" (onClick)="cancel()" 
                severity="secondary" [outlined]="true"></p-button>
              <p-button label="Next: Build Form" icon="pi pi-arrow-right" 
                [disabled]="!form.form_name || !form.module_name" (onClick)="activeTab = 1"></p-button>
            </div>
          </div>
        </div>
      </p-tabpanel>

      <!-- Tab 2: Form Builder -->
      <p-tabpanel [value]="1">
        <div class="form-builder-layout grid grid-cols-12 gap-4 h-[70vh]">
          
          <!-- Left Panel: Available Fields -->
          <div class="col-span-3 bg-gray-50 rounded-lg p-4 overflow-y-auto border">
            <div class="flex items-center justify-between mb-4">
              <h3 class="text-lg font-semibold">Available Fields</h3>
              <i class="pi pi-info-circle text-gray-400" pTooltip="Drag fields to the canvas"></i>
            </div>
            
            <p-divider></p-divider>

            <div *ngIf="loadingFields" class="text-center py-4">
              <i class="pi pi-spin pi-spinner text-2xl"></i>
              <p class="text-sm text-gray-500 mt-2">Loading fields...</p>
            </div>

            <div *ngIf="!loadingFields && availableFields.length === 0" class="text-center py-4">
              <i class="pi pi-inbox text-4xl text-gray-300"></i>
              <p class="text-sm text-gray-500 mt-2">No fields available</p>
            </div>

            <div cdkDropList #availableFieldsList="cdkDropList" 
              id="available-fields"
              [cdkDropListData]="availableFields"
              [cdkDropListConnectedTo]="connectedDropListIds"
              [cdkDropListSortingDisabled]="true"
              class="field-list space-y-2">
              
              <div *ngFor="let field of availableFields; trackBy: trackByFieldId"
                cdkDrag
                class="field-item bg-white border rounded-lg p-3 cursor-move hover:shadow-md transition-shadow">
                <div class="flex items-center justify-between">
                  <div class="flex-1">
                    <div class="font-medium text-sm">{{ getFieldLabel(field) }}</div>
                    <div class="text-xs text-gray-500 mt-1">
                      {{ field.type || field.field }}
                    </div>
                  </div>
                  <i class="pi pi-arrows-alt text-gray-400"></i>
                </div>
              </div>
            </div>
          </div>

          <!-- Center Panel: Form Canvas -->
          <div class="col-span-6 bg-white rounded-lg p-4 overflow-y-auto border">
            <div class="flex items-center justify-between mb-4">
              <h3 class="text-lg font-semibold">Form Canvas</h3>
              <p-button label="Add Row" icon="pi pi-plus" (onClick)="addNewRow(2)" 
                [text]="true" size="small"></p-button>
            </div>

            <p-divider></p-divider>

            <!-- Empty State -->
            <div *ngIf="formRows.length === 0" class="text-center py-12">
              <i class="pi pi-file text-6xl text-gray-300"></i>
              <p class="text-gray-500 mt-4">Start building your form by adding rows</p>
              <p-button label="Add First Row" icon="pi pi-plus" (onClick)="addNewRow(2)" 
                class="mt-4"></p-button>
            </div>

            <!-- Form Rows -->
            <div class="space-y-4">
              <div *ngFor="let row of formRows; let rowIndex = index; trackBy: trackByRowId"
                class="form-row border rounded-lg p-3 bg-gray-50">
                
                <!-- Row Header -->
                <div class="flex items-center justify-between mb-3">
                  <div class="flex items-center gap-2">
                    <span class="text-sm font-semibold text-gray-600">Row {{ rowIndex + 1 }}</span>
                    <p-select [(ngModel)]="row.columns" [options]="columnOptions"
                      (onChange)="changeRowColumns(row, row.columns)"
                      class="row-columns-select" [style]="{'width': '150px'}">
                    </p-select>
                  </div>

                  <div class="flex gap-1">
                    <p-button icon="pi pi-arrow-up" [text]="true" size="small"
                      (onClick)="moveRowUp(rowIndex)" [disabled]="rowIndex === 0"
                      pTooltip="Move row up"></p-button>
                    <p-button icon="pi pi-arrow-down" [text]="true" size="small"
                      (onClick)="moveRowDown(rowIndex)" [disabled]="rowIndex === formRows.length - 1"
                      pTooltip="Move row down"></p-button>
                    <p-button icon="pi pi-trash" [text]="true" severity="danger" size="small"
                      (onClick)="deleteRow(rowIndex)" pTooltip="Delete row"></p-button>
                  </div>
                </div>

                <!-- Drop Zone -->
                <div cdkDropList
                  [id]="getRowDropZoneId(rowIndex)"
                  [cdkDropListData]="row.fields"
                  [cdkDropListConnectedTo]="['available-fields']"
                  (cdkDropListDropped)="onFieldDropped($event, row)"
                  class="drop-zone grid gap-3"
                  [ngClass]="{
                    'grid-cols-1': row.columns === 1,
                    'grid-cols-2': row.columns === 2,
                    'grid-cols-3': row.columns === 3
                  }"
                  [class.empty-drop-zone]="row.fields.length === 0">
                  
                  <!-- Empty state -->
                  <div *ngIf="row.fields.length === 0" 
                    class="empty-drop-message col-span-full text-center py-8 border-2 border-dashed border-gray-300 rounded">
                    <i class="pi pi-inbox text-3xl text-gray-300"></i>
                    <p class="text-sm text-gray-500 mt-2">Drag fields here</p>
                  </div>

                  <!-- Fields in row -->
                  <div *ngFor="let field of row.fields; trackBy: trackByFieldId"
                    cdkDrag
                    class="field-card bg-white border-2 border-blue-200 rounded p-3 relative">
                    
                    <div class="field-card-header flex items-start justify-between mb-2">
                      <div class="flex-1">
                        <div class="font-medium text-sm text-gray-800">
                          {{ getFieldLabel(field.field) }}
                        </div>
                        <div class="text-xs text-gray-500 mt-1">
                          {{ field.ui_type }} 
                          <span *ngIf="field.is_required" class="text-red-500">*</span>
                        </div>
                      </div>
                      
                      <div class="flex gap-1 mr-5">
                        <button class="text-green-500 hover:text-green-700" 
                          (click)="editFieldLabel(field)"
                          pTooltip="Edit label">
                          <i class="pi pi-pencil text-sm"></i>
                        </button>
                        <button class="text-blue-500 hover:text-blue-700" 
                          (click)="configureField(field)"
                          pTooltip="Configure field">
                          <i class="pi pi-cog text-sm"></i>
                        </button>
                        <button class="text-red-500 hover:text-red-700" 
                          (click)="removeField(row, field)"
                          pTooltip="Remove field">
                          <i class="pi pi-times text-sm"></i>
                        </button>
                      </div>
                    </div>

                    <!-- Field Preview -->
                    <div class="field-preview text-xs text-gray-400 italic">
                      <div *ngIf="field.placeholder">{{ field.placeholder }}</div>
                      <div *ngIf="field.help_text" class="mt-1">{{ field.help_text }}</div>
                    </div>

                    <!-- Drag handle -->
                    <div cdkDragHandle class="drag-handle absolute top-2 right-2 cursor-move text-gray-400">
                      <i class="pi pi-arrows-alt"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Right Panel: Form Preview/Info -->
          <div class="col-span-3 bg-gray-50 rounded-lg p-4 overflow-y-auto border">
            <h3 class="text-lg font-semibold mb-4">Form Info</h3>
            <p-divider></p-divider>

            <div class="space-y-4 text-sm">
              <div>
                <label class="font-semibold text-gray-600">Form Name:</label>
                <p class="mt-1">{{ form.form_name || 'Not set' }}</p>
              </div>

              <div>
                <label class="font-semibold text-gray-600">Module:</label>
                <p class="mt-1">{{ getModuleName(form.module_id) }}</p>
              </div>

              <div>
                <label class="font-semibold text-gray-600">Total Fields:</label>
                <p class="mt-1">{{ getTotalFields() }}</p>
              </div>

              <div>
                <label class="font-semibold text-gray-600">Total Rows:</label>
                <p class="mt-1">{{ formRows.length }}</p>
              </div>

              <div>
                <label class="font-semibold text-gray-600">Status:</label>
                <p class="mt-1">
                  <span [class]="form.is_published ? 'text-green-600' : 'text-orange-600'">
                    {{ form.is_published ? 'Published' : 'Draft' }}
                  </span>
                </p>
              </div>
            </div>

            <p-divider></p-divider>

            <div class="mt-4 p-3 bg-blue-50 rounded text-xs text-blue-800">
              <i class="pi pi-info-circle mr-1"></i>
              <strong>Tips:</strong>
              <ul class="mt-2 space-y-1 ml-4 list-disc">
                <li>Drag fields from left panel to canvas</li>
                <li>Click configure icon to customize fields</li>
                <li>Change row layout using dropdown</li>
                <li>Reorder fields by dragging within rows</li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="flex justify-between mt-6 pt-4 border-t">
          <p-button label="Back to Basic Info" icon="pi pi-arrow-left" 
            [text]="true" (onClick)="activeTab = 0"></p-button>
          
          <div class="flex gap-2">
            <p-button label="Cancel" (onClick)="cancel()" 
              severity="secondary" [outlined]="true" [disabled]="savingForm"></p-button>
            <p-button [label]="isEditMode ? 'Update' : 'Save as Draft'" icon="pi pi-save" 
              (onClick)="saveForm()" [loading]="savingForm"></p-button>
          </div>
        </div>
      </p-tabpanel>
    </p-tabpanels>
  </p-tabs>
</div>

<!-- Field Configuration Dialog -->
<p-dialog [(visible)]="showFieldConfigDialog" [modal]="true" 
  [style]="{ width: '600px' }" [draggable]="false" [resizable]="false"
  header="Configure Field" appendTo="body">
  
  <div class="dialog-content">
  <div *ngIf="selectedField" class="space-y-4">
    <div>
      <label class="block mb-2 font-semibold">Field Label</label>
      <p class="text-sm text-gray-600">{{ getFieldLabel(selectedField.field) }}</p>
    </div>

    <div>
      <label class="block mb-2 font-semibold">UI Type</label>
      <p-select [(ngModel)]="selectedField.ui_type" [options]="uiTypeOptions"
        class="w-full" placeholder="Select UI type">
      </p-select>
    </div>

    <div>
      <label class="block mb-2 font-semibold">Placeholder</label>
      <input pInputText [(ngModel)]="selectedField.placeholder" class="w-full"
        placeholder="Enter placeholder text" />
    </div>

    <div>
      <label class="block mb-2 font-semibold">Help Text</label>
      <textarea pInputTextarea [(ngModel)]="selectedField.help_text" class="w-full" rows="2"
        placeholder="Enter help text (optional)"></textarea>
    </div>

    <div class="flex items-center gap-2">
      <p-checkbox [(ngModel)]="selectedField.is_required" [binary]="true" 
        inputId="required"></p-checkbox>
      <label for="required" class="cursor-pointer">Required field</label>
    </div>

    <!-- Options for select/radio/multiselect -->
    <div *ngIf="showOptions(selectedField)">
      <label class="block mb-2 font-semibold">Options</label>
      <div class="p-3 bg-blue-50 border border-blue-200 rounded mb-3">
        <p class="text-sm text-blue-800">
          <i class="pi pi-info-circle mr-1"></i>
          Add custom options for this field. Each option will appear as a choice in the form.
        </p>
      </div>
      <div class="space-y-2">
        <div *ngFor="let option of getFieldOptions(selectedField); let i = index; trackBy: trackByIndex" class="flex gap-2">
          <input pInputText [value]="option" (input)="updateOption(i, $event)" class="flex-1"
            placeholder="Option {{ i + 1 }}" />
          <p-button icon="pi pi-trash" severity="danger" [text]="true"
            (onClick)="removeOption(i)"></p-button>
        </div>
        <p-button label="Add Option" icon="pi pi-plus" [text]="true" size="small"
          (onClick)="addOption()"></p-button>
      </div>
    </div>
  </div>


  <!-- <ng-template pTemplate="footer"> -->
    <div class="flex justify-end gap-2">
      <p-button label="Cancel" severity="secondary" [outlined]="true"
        (onClick)="showFieldConfigDialog = false"></p-button>
      <p-button label="Save Configuration" (onClick)="saveFieldConfig()"></p-button>
    </div>
  <!-- </ng-template> -->
    </div>
</p-dialog>

<!-- Label Edit Dialog -->
<p-dialog [(visible)]="showLabelEditDialog" [modal]="true" 
  [style]="{ width: '400px' }" [draggable]="false" [resizable]="false"
  header="Edit Field Label">
  
  <div class="space-y-4 dialog-content">
    <div>
      <label class="block mb-2 font-semibold">Field Label</label>
      <input pInputText [(ngModel)]="labelEditValue" class="w-full"
        placeholder="Enter new label" />
    </div>
  </div>

  <ng-template pTemplate="footer">
    <div class="flex justify-end gap-2">
      <p-button label="Cancel" severity="secondary" [outlined]="true"
        (onClick)="showLabelEditDialog = false"></p-button>
      <p-button label="Save Label" (onClick)="saveFieldLabel()"></p-button>
    </div>
  </ng-template>
</p-dialog>
